// see .storybook/preview.ts
import '@/styles/global.scss';
import '@/assets/styles/main.css';
import '@vuepic/vue-datepicker/dist/main.css';
import '@/assets/styles/datepicker.scss';
import 'vue3-carousel/dist/carousel.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet.fullscreen/Control.FullScreen.css';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { AxiosError, isAxiosError, isCancel } from 'axios';
import { abilitiesPlugin } from '@casl/vue';
import Notifications from '@kyvg/vue3-notification';
import * as Sentry from '@sentry/vue';

import eventBusPlugin from '@/plugins/eventBus';
import vuetify from '@/plugins/vuetify';
import App from '@/App.vue';
import router from '@/router';
import { key, store } from '@/store'; // DEPRECATED
import { createEmptyAbility } from '@/helpers/casl';
import { brandConfig } from '@/brand';

const ability = createEmptyAbility();

const pinia = createPinia();
const app = createApp(App);

// prettier-ignore
app
  .use(eventBusPlugin)
  .use(Notifications)
  .use(pinia)
  .use(router)
  .use(store, key) // DEPRECATED
  .use(vuetify)
  .use(abilitiesPlugin, ability);

function handleSentryBeforeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
  hint?: Sentry.BreadcrumbHint,
): Sentry.Breadcrumb | null {
  if (breadcrumb.category === 'xhr') {
    if (breadcrumb.data?.url?.includes('token')) return breadcrumb; // Do not capture body for token requests

    if (breadcrumb.data?.status_code >= 400) {
      // Capture response body for requests with status code >= 400
      const data = {
        requestBody: hint?.xhr.__sentry_xhr_v2__.body,
        response: hint?.xhr.response,
      };
      return { ...breadcrumb, data };
    } else {
      // else only capture request body
      const data = {
        requestBody: hint?.xhr.__sentry_xhr_v2__.body,
      };
      return { ...breadcrumb, data };
    }
  }

  // if (breadcrumb.category === 'ui.input') return null; // Do not capture input events

  // Capture text inside interactive elements
  if (breadcrumb.category === 'ui.click' && hint?.event.target) {
    // const isInteractiveElement = hint.event.target.tabIndex > -1;
    const innerText = (hint.event.target.innerText || '') as string;
    const hasTextInside = Boolean(innerText);
    if (hasTextInside) {
      breadcrumb.message = (breadcrumb.message || '') + `{"${innerText}"}`;
    }
  }
  return breadcrumb;
}

function handleSentryBeforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent | PromiseLike<Sentry.ErrorEvent | null> | null {
  // We cancel the request in some cases, so we don't want to capture it in Sentry
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (isCancel(hint?.originalException)) {
    // console.debug('Cancelled request ignored by Sentry', hint.originalException);
    return null;
  }
  // Most of our fetchers or components functions are throwing Error in the end of the Promise cycle
  // if the error is uknown, it will be caught by Sentry by default, but we don't want to capture 401 errors
  // as they are not critical. That is why we are ignoring them here.
  if (
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    hint?.originalException instanceof AxiosError &&
    hint.originalException.response?.status === 401
  ) {
    // console.debug('401 error ignored by Sentry', hint.originalException);
    return null;
  }
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  addAxiosContextRecursive(event, hint?.originalException);
  return event;
}

function addAxiosContextRecursive(
  event: Sentry.ErrorEvent,
  error: unknown,
): void {
  if (isAxiosError(error)) {
    addAxiosContext(event, error);
  } else if (error instanceof Error && error.cause) {
    addAxiosContextRecursive(event, error.cause);
  }
}

function addAxiosContext(event: Sentry.ErrorEvent, error: AxiosError): void {
  if (error.response) {
    const contexts = { ...event.contexts };
    contexts.Axios = { Response: error.response };
    event.contexts = contexts;
  }
}

if (import.meta.env.VITE_APP_SENTRY_ON !== 'false') {
  Sentry.init({
    app,

    attachProps: true,
    logErrors: true,
    trackComponents: true,
    tracingOptions: {
      trackComponents: true,
    },

    dsn: import.meta.env.VITE_APP_SENTRY_PUBLIC_KEY,
    environment: import.meta.env.VITE_APP_SENTRY_ENVIRONMENT,

    release: brandConfig.sentryRelease,

    replaysSessionSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,

    tracesSampleRate: 0.2,
    tracePropagationTargets: [
      'localhost',
      'kraken.kkts.com.au/api',
      'dashtest.kkts.com.au/api',
    ],

    integrations: [
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        networkDetailAllowUrls: [
          'kraken.kkts.com.au/api',
          'dashtest.kkts.com.au/api',
        ],
        networkDetailDenyUrls: [
          'kraken.kkts.com.au/api/token',
          'dashtest.kkts.com.au/api/token',
        ],
      }),
      Sentry.browserTracingIntegration({ router }),
    ],

    beforeSend: handleSentryBeforeSend,
    beforeBreadcrumb: handleSentryBeforeBreadcrumb,
  });
}

app.mount('#app');
